"use client";

import { useMemo } from "react";
import { useSelector } from "react-redux";

import { OptimizelyExperimentsEnum } from "@/config/app-constant";
import { useGatekeeperQuestions } from "@/providers/gatekeeper-questions-provider";
import { RootState } from "@/store/store";

export enum MedicalConfirmationFlowEnum {
  withoutUndiagnosedSymptomsQuestion = "control",
  withUndiagnosedSymptomsQuestion = "variant",
}

type RetrievedMedicalConfirmationFlowValue = MedicalConfirmationFlowEnum | null;

const withUndiagnosedSymptoms = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return function WithUndiagnosedSymptomsFlowComponent(props: P) {
    // flow selection  logic
    // check in magnolia res experiment enabled (undiagnosedQuestionExperimentEnabled === true). then,
    //    1. check if flow is defined on BE quote response
    //    2. check if optimizely flag set
    //    3. check magnolia res if undiagnosed symptoms question enabled (undiagnosedQuestionEnabled === true)
    // Else,
    //    1. check magnolia res if undiagnosed symptoms question enabled (undiagnosedQuestionEnabled === true)

    // retrieving backend flag
    const experimentsFromBE = useSelector(
      (state: RootState) => state?.quote?.attributes?.experimentDetails
    );

    const flowValueFromBE = useMemo(() => {
      return experimentsFromBE?.undiagnosedFlow as MedicalConfirmationFlowEnum;
    }, [experimentsFromBE]);

    // retrieving magnolia flags
    const { undiagnosedQuestionEnabled, undiagnosedQuestionExperimentEnabled } =
      useGatekeeperQuestions();

    const flowValueFromMagnolia: MedicalConfirmationFlowEnum = useMemo(() => {
      return undiagnosedQuestionEnabled
        ? MedicalConfirmationFlowEnum.withUndiagnosedSymptomsQuestion
        : MedicalConfirmationFlowEnum.withoutUndiagnosedSymptomsQuestion;
    }, [undiagnosedQuestionEnabled]);

    // retrieving local storage flag
    const retrieveFlowFromLocalStorage =
      (): RetrievedMedicalConfirmationFlowValue => {
        try {
          return (
            (localStorage.getItem(
              OptimizelyExperimentsEnum.UNDIAGNOSED
            ) as MedicalConfirmationFlowEnum) || null
          );
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error retrieving flow from local storage:", error);
          return null;
        }
      };

    const flowValueFromLocalStorage: RetrievedMedicalConfirmationFlowValue =
      useMemo(() => {
        return retrieveFlowFromLocalStorage();
      }, []);

    // flow derivation logic
    const deriveFlow = (
      flowValue: RetrievedMedicalConfirmationFlowValue,
      fallbackFlow: MedicalConfirmationFlowEnum
    ): MedicalConfirmationFlowEnum => {
      return flowValue ===
        MedicalConfirmationFlowEnum.withUndiagnosedSymptomsQuestion ||
        flowValue ===
          MedicalConfirmationFlowEnum.withoutUndiagnosedSymptomsQuestion
        ? flowValue
        : fallbackFlow;
    };

    const selectedFlow: MedicalConfirmationFlowEnum = useMemo(() => {
      if (undiagnosedQuestionExperimentEnabled) {
        return deriveFlow(
          flowValueFromBE,
          deriveFlow(flowValueFromLocalStorage, flowValueFromMagnolia)
        );
      }
      return flowValueFromMagnolia;
    }, [
      undiagnosedQuestionExperimentEnabled,
      flowValueFromBE,
      flowValueFromLocalStorage,
      flowValueFromMagnolia,
    ]);

    return (
      <Component
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        medicalConfirmationFlow={selectedFlow}
        undiagnosedQuestionExperimentEnabled={
          undiagnosedQuestionExperimentEnabled
        }
      />
    );
  };
};

export default withUndiagnosedSymptoms;
